/**
 * CAI Project Workspace — 7-tab project interface
 * All project functionality lives here (no scattering across pages)
 * /cai/projects/:id
 */
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import client from "@/api/client";
import { toast } from "sonner";

import { ArrowLeft, ChevronRight, RefreshCw, Check, CheckCircle2, X, AlertTriangle, Info, Shield, Target, Leaf, BarChart2, FileText, ArrowUpRight, Plus, FileDown } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
const LIFECYCLE_STAGES = ["INTAKE","SCREENING","STRUCTURING","MRV_SETUP","MRV_ACTIVE","VERIFICATION","CLASSIFICATION","VALUE_READY","ARTICLE6_SUBMISSION","ISSUED"];
const STAGE_COLORS = { INTAKE:"bg-slate-400", SCREENING:"bg-blue-400", STRUCTURING:"bg-indigo-400", MRV_SETUP:"bg-violet-400", MRV_ACTIVE:"bg-purple-400", VERIFICATION:"bg-amber-400", CLASSIFICATION:"bg-orange-400", VALUE_READY:"bg-emerald-500", ARTICLE6_SUBMISSION:"bg-teal-500", ISSUED:"bg-emerald-600" };
const PATHWAY_CFG = {
  article6_candidate:       { color:"text-teal-700 bg-teal-50 border-teal-200",     label:"Article 6 Candidate" },
  carbon_credit_candidate:  { color:"text-amber-700 bg-amber-50 border-amber-200",  label:"Carbon Credit Candidate" },
  climate_performance_asset:{ color:"text-blue-700 bg-blue-50 border-blue-200",     label:"Climate Performance Asset" },
};
const fmt = (n, d=1) => n != null && !isNaN(n) ? (+n).toFixed(d) : "—";

// ── Stage Progress Bar ─────────────────────────────────────────────────────
function StageProgress({ currentStage }) {
  const idx = LIFECYCLE_STAGES.indexOf(currentStage);
  return (
    <div className="flex items-center gap-1 overflow-x-auto pb-1" data-testid="stage-progress">
      {LIFECYCLE_STAGES.map((s, i) => {
        const done    = i < idx;
        const current = i === idx;
        return (
          <React.Fragment key={s}>
            {i > 0 && <div className={`h-0.5 w-4 flex-shrink-0 ${i <= idx ? "bg-[#059669]" : "bg-slate-200"}`} />}
            <div className={`flex flex-col items-center flex-shrink-0`}>
              <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold
                ${current ? "bg-[#059669] text-white ring-2 ring-emerald-200" : done ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>
                {done ? <Check size={10} strokeWidth={3} /> : i+1}
              </div>
              <p className={`text-xs mt-0.5 whitespace-nowrap ${current ? "text-[#059669] font-bold" : done ? "text-slate-500" : "text-slate-300"}`}>
                {s.replace(/_/g," ")}
              </p>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── CARS Score Panel ───────────────────────────────────────────────────────
function CARSPanel({ project, onRecompute }) {
  const [loading, setLoading] = useState(false);
  const score = project.cars_score || 0;
  const color = score >= 75 ? "#059669" : score >= 50 ? "#3B82F6" : score >= 30 ? "#F59E0B" : "#EF4444";

  const compute = async () => {
    setLoading(true);
    try {
      const res = await client.get(`/cai/projects/${project.id}/cars`);
      toast.success(`CARS: ${res.data.cars_score?.toFixed(0)}/100`);
      onRecompute(res.data);
    } catch { toast.error("CARS computation failed"); }
    setLoading(false);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="cars-panel">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-bold text-slate-900">Climate Asset Readiness (CARS)</h4>
        <button onClick={compute} disabled={loading} className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-700">
          <RefreshCw size={11} className={loading?"animate-spin":""} /> Recompute
        </button>
      </div>
      <div className="flex items-center gap-4 mb-3">
        <div className="relative flex-shrink-0" style={{width:64,height:64}}>
          <svg width="64" height="64" viewBox="0 0 64 64">
            <circle cx="32" cy="32" r="24" fill="none" stroke="#E2E8F0" strokeWidth="6" />
            <circle cx="32" cy="32" r="24" fill="none" stroke={color} strokeWidth="6"
              strokeDasharray={`${(score/100)*150.8} 150.8`} strokeDashoffset="37.7" strokeLinecap="round" />
            <text x="32" y="29" textAnchor="middle" fill="#0F172A" fontSize="12" fontWeight="700">{Math.round(score)}</text>
            <text x="32" y="41" textAnchor="middle" fill="#94A3B8" fontSize="7">/100</text>
          </svg>
        </div>
        <div className="flex-1 space-y-1.5">
          {project.cars_gaps?.slice(0,2).map((g,i) => (
            <div key={i} className="flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded">
              <AlertTriangle size={10} className="mt-0.5 flex-shrink-0" />{g}
            </div>
          ))}
        </div>
      </div>
      {project.cars_next_actions?.slice(0,2).map((a,i) => (
        <p key={i} className="text-xs text-slate-500 flex items-start gap-1.5 mt-1">
          <ChevronRight size={10} className="mt-0.5 flex-shrink-0 text-[#059669]" />{a}
        </p>
      ))}
    </div>
  );
}

// ── ITMO Sovereign Transfer Panel ─────────────────────────────────────────
function ITMOPanel({ projectDbId, onRefresh }) {
  const [itmos,    setItmos]    = useState([]);
  const [gate,     setGate]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form,     setForm]     = useState({ buyer_country:"", buyer_entity:"", volume_tco2e:"", vintage_year:"", pathway:"art6_2", notes:"" });

  useEffect(() => {
    setLoading(true);
    client.get(`/cai/itmo/${projectDbId}`)
      .then(r => { setItmos(r.data.itmos || []); setGate(r.data.gate); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [projectDbId]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const res = await client.post(`/cai/itmo/${projectDbId}`, {
        ...form,
        volume_tco2e: Number(form.volume_tco2e),
        vintage_year: Number(form.vintage_year),
      });
      toast.success(`ITMO ${res.data.itmo_id} recorded. Corresponding adjustment applied.`);
      setShowForm(false);
      setItmos(prev => [...prev, res.data.itmo]);
      onRefresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "object" && detail.blockers) {
        toast.error(`${detail.gate_count} gate(s) not met: ${detail.blockers[0]?.gate}`);
      } else {
        toast.error(String(detail || "ITMO creation failed"));
      }
    }
    setCreating(false);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 mt-0" data-testid="itmo-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-sm font-bold text-slate-900">Sovereign ITMO Transfer Module</p>
          <p className="text-xs text-slate-400 mt-0.5">Article 6 ITMOs — distinct from voluntary credits</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} data-testid="create-itmo-btn"
          className="flex items-center gap-1 px-3 py-1.5 bg-teal-600 text-white text-xs font-medium rounded-lg hover:bg-teal-700">
          <Plus size={12}/> Record ITMO Transfer
        </button>
      </div>

      {/* ITMO list */}
      {itmos.length === 0 ? (
        <p className="text-xs text-slate-400 italic text-center py-4">No ITMO transfers recorded yet.</p>
      ) : (
        <div className="space-y-2 mb-3">
          {itmos.map(it => (
            <div key={it.itmo_id} className="border border-teal-100 bg-teal-50/50 rounded-xl p-3" data-testid={`itmo-${it.itmo_id}`}>
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-xs font-bold font-mono text-teal-700">{it.itmo_id}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{it.host_country} → {it.buyer_country} ({it.buyer_entity})</p>
                  <p className="text-xs text-slate-400">{it.pathway} · Vintage {it.vintage_year} · CA applied: {it.is_ca_applied ? "Yes" : "No"}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-teal-700">{it.volume_tco2e} t</p>
                  <p className="text-xs text-slate-400">tCO₂e</p>
                </div>
              </div>
              {it.corresponding_adjustment && (
                <div className="mt-2 text-xs text-slate-400 bg-white rounded p-1.5 border border-slate-100">
                  CA: Host {it.corresponding_adjustment.host_adjustment}t / Buyer +{it.corresponding_adjustment.buyer_adjustment}t · {it.corresponding_adjustment.reporting_cycle} · Status: {it.corresponding_adjustment.reporting_status}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create ITMO form */}
      {showForm && (
        <div className="border border-teal-200 bg-teal-50/30 rounded-xl p-4 space-y-3">
          <p className="text-xs font-semibold text-teal-700">Record ITMO Transfer</p>
          <div className="grid grid-cols-2 gap-4">
            {[["buyer_country","Buyer Country","Switzerland"],["buyer_entity","Buyer Entity","BAFU / Swiss Climate Fund"],["volume_tco2e","Volume (tCO₂e)","50"],["vintage_year","Vintage Year","2023"]].map(([k,l,p])=>(
              <div key={k}>
                <label className="text-xs text-slate-500 mb-0.5 block">{l}</label>
                <input value={form[k]} onChange={e=>setForm(f=>({...f,[k]:e.target.value}))} placeholder={p}
                  className="w-full h-8 px-2 text-xs border border-slate-200 rounded-lg focus:outline-none"/>
              </div>
            ))}
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-0.5 block">Pathway</label>
            <select value={form.pathway} onChange={e=>setForm(f=>({...f,pathway:e.target.value}))}
              className="w-full h-8 px-2 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none">
              <option value="art6_2">Article 6.2 (Bilateral cooperative)</option>
              <option value="art6_4">Article 6.4 (UNFCCC supervised mechanism)</option>
            </select>
          </div>
          <div className="flex gap-2">
            <button onClick={handleCreate} disabled={creating}
              className="flex-1 h-9 bg-teal-600 text-white text-xs font-medium rounded-lg hover:bg-teal-700 disabled:opacity-50">
              {creating ? "Recording..." : "Record Sovereign Transfer"}
            </button>
            <button onClick={() => setShowForm(false)} className="px-3 h-9 border border-slate-200 text-slate-600 text-xs rounded-lg hover:bg-slate-50">Cancel</button>
          </div>
          <p className="text-xs text-slate-400 italic">Corresponding adjustment (host -volume, buyer +volume) will be automatically recorded.</p>
        </div>
      )}
    </div>
  );
}

// ── Article 6 Panel ──────────────────────────────────────────────────────
function Article6Panel({ projectDbId, article6Score, onRefresh }) {
  const [a6Data,    setA6Data]    = useState(null);
  const [loa,       setLoa]       = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [updating,  setUpdating]  = useState(false);
  const [editForm,  setEditForm]  = useState({});
  const [showEdit,  setShowEdit]  = useState(false);
  const [advancing, setAdvancing] = useState(false);
  const [advForm,   setAdvForm]   = useState({ stage: "", notes: "" });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  // Load current A6 data and LoA on mount
  useEffect(() => {
    Promise.allSettled([
      client.get(`/cai/projects/${projectDbId}/article6`),
      client.get(`/cai/loa/${projectDbId}`),
    ]).then(([a6Res, loaRes]) => {
      if (a6Res.status === "fulfilled") setA6Data(a6Res.value.data);
      if (loaRes.status === "fulfilled") setLoa(loaRes.value.data);
    });
  }, [projectDbId]);

  const runCheck = async () => {
    setLoading(true);
    try {
      const [a6Res, loaRes] = await Promise.allSettled([
        client.post(`/cai/projects/${projectDbId}/article6/check`),
        client.get(`/cai/loa/${projectDbId}`),
      ]);
      if (a6Res.status === "fulfilled") {
        const d = a6Res.value.data;
        setA6Data({ ...d, eligibility: d.eligibility || d });
        toast.success(`A6 Maturity: ${d.a6_readiness_score?.toFixed(0)}/100 | Auth: ${d.is_authorized ? "AUTHORIZED" : "Not yet authorized"}`);
      }
      if (loaRes.status === "fulfilled") setLoa(loaRes.value.data);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "A6 check failed"); }
    setLoading(false);
  };

  const initiateLoa = async () => {
    try {
      const res = await client.post(`/cai/loa/${projectDbId}`, {
        host_country: a6Data?.ndc_reference?.geo,
        dna_authority: a6Data?.ndc_reference?.dna_name,
      });
      setLoa(res.data);
      toast.success("LoA application initiated. Stage: PREPARATION.");
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to initiate LoA"); }
  };

  const saveLoaFields = async () => {
    setUpdating(true);
    try {
      const res = await client.patch(`/cai/loa/${projectDbId}`, editForm);
      setLoa(res.data);
      toast.success("LoA fields saved");
      setShowEdit(false);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    setUpdating(false);
  };

  const advanceStage = async () => {
    if (!advForm.stage) { toast.warning("Select target stage"); return; }
    setAdvancing(true);
    try {
      const res = await client.post(`/cai/loa/${projectDbId}/advance`, { target_stage: advForm.stage, notes: advForm.notes });
      toast.success(res.data.message);
      setLoa(l => l ? {...l, lifecycle_stage: res.data.new_stage} : l);
      setAdvForm({stage:"",notes:""});
      // Refresh A6 data
      const a6Res = await client.post(`/cai/projects/${projectDbId}/article6/check`);
      setA6Data({...a6Res.data, eligibility: a6Res.data.eligibility || a6Res.data});
      onRefresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "object" ? detail.message : String(detail || "Failed"));
    }
    setAdvancing(false);
  };

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("document_type", "loa_application");
    try {
      await client.post(`/cai/loa/${projectDbId}/documents`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(`Document uploaded: ${file.name}`);
      const loaRes = await client.get(`/cai/loa/${projectDbId}`);
      setLoa(loaRes.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    setUploading(false);
  };

  const score = a6Data?.a6_readiness_score || article6Score || 0;
  const comps = a6Data?.components || {};
  const elig  = a6Data?.eligibility || (a6Data?.blockers !== undefined ? a6Data : {});
  const isAuthorized = a6Data?.is_authorized || false;
  const a6Status = a6Data?.article6_status || "potential";
  const color = score >= 80 ? "#059669" : score >= 60 ? "#3B82F6" : "#F59E0B";

  const LOA_STAGES = ["NOT_INITIATED","PREPARATION","SUBMITTED","UNDER_REVIEW","APPROVED","REJECTED"];
  const loaIdx = loa?.lifecycle_stage ? LOA_STAGES.indexOf(loa.lifecycle_stage) : 0;

  return (
    <div data-testid="article6-panel">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Article 6 Governance</p>
        <button onClick={runCheck} disabled={loading} data-testid="run-a6-check-btn"
          className="flex items-center gap-1 px-2.5 py-1.5 bg-teal-600 text-white text-xs font-medium rounded-lg hover:bg-teal-700 disabled:opacity-50">
          <RefreshCw size={11} className={loading?"animate-spin":""}/> Run A6 Check
        </button>
      </div>

      {/* ── CRITICAL: Hard Gate Banner ── */}
      <div className={`rounded-xl border-2 p-3 mb-3 flex items-start gap-2.5 ${isAuthorized ? "border-emerald-400 bg-emerald-50" : "border-red-300 bg-red-50"}`}
        data-testid="loa-authorization-gate">
        {isAuthorized
          ? <CheckCircle2 size={18} className="text-[#059669] flex-shrink-0 mt-0.5"/>
          : <X size={18} className="text-red-500 flex-shrink-0 mt-0.5"/>}
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-bold ${isAuthorized ? "text-emerald-700" : "text-red-700"}`}>
            {isAuthorized ? "ITMO Export AUTHORIZED" : "NOT AUTHORIZED FOR ITMO TRANSFER"}
          </p>
          <p className={`text-xs mt-0.5 ${isAuthorized ? "text-emerald-600" : "text-red-600"}`}>
            {isAuthorized
              ? "LoA issued by host country DNA AND independently verified. Article 6 transfer enabled."
              : (a6Data?.blocking_requirement || "LoA (Letter of Authorization) from host country DNA required + document verification")}
          </p>
          {/* Two-factor breakdown */}
          <div className="flex gap-3 mt-2 text-xs">
            <span className={`flex items-center gap-1 ${a6Data?.loa_approved ? "text-emerald-600" : "text-slate-400"}`}>
              {a6Data?.loa_approved ? <CheckCircle2 size={9}/> : <X size={9}/>} LoA APPROVED
            </span>
            <span className={`flex items-center gap-1 ${a6Data?.loa_verified ? "text-emerald-600" : "text-red-500"}`}>
              {a6Data?.loa_verified ? <CheckCircle2 size={9}/> : <X size={9}/>} Document Verified
            </span>
          </div>
          {!isAuthorized && (
            <p className="text-xs text-slate-400 mt-1 italic">
              A6RS is a maturity indicator only. Both LoA APPROVED and document verification required for authorization.
            </p>
          )}
        </div>
      </div>

      {/* A6 Maturity Score */}
      {score > 0 && (
        <div className="flex items-center gap-4 mb-3 p-3 bg-slate-50 rounded-xl border border-slate-200">
          <div className="relative flex-shrink-0" style={{width:56,height:56}}>
            <svg width="56" height="56" viewBox="0 0 56 56">
              <circle cx="28" cy="28" r="20" fill="none" stroke="#E2E8F0" strokeWidth="5"/>
              <circle cx="28" cy="28" r="20" fill="none" stroke={color} strokeWidth="5"
                strokeDasharray={`${(score/100)*125.7} 125.7`} strokeDashoffset="31.4" strokeLinecap="round"/>
              <text x="28" y="26" textAnchor="middle" fill="#0F172A" fontSize="9" fontWeight="700">{Math.round(score)}</text>
              <text x="28" y="35" textAnchor="middle" fill="#94A3B8" fontSize="6">/100</text>
            </svg>
          </div>
          <div className="flex-1">
            <p className="text-xs font-semibold text-slate-600 mb-1">A6 Maturity Score (not authorization)</p>
            {[["NDC Alignment",30,comps.ndc_alignment],["Additionality",25,comps.additionality_beyond_ndc],
              ["DNA Capacity",20,comps.host_country_capacity],["Data Quality",15,comps.data_quality],
              ["LoA Momentum",10,comps.loa_momentum]].map(([lbl,max,val]) => (
              <div key={lbl} className="flex items-center gap-2 mb-0.5">
                <span className="text-xs text-slate-400 w-22">{lbl}</span>
                <div className="flex-1 h-1 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-teal-400" style={{width:`${((val||0)/max)*100}%`}}/>
                </div>
                <span className="text-xs font-bold text-teal-700 w-8 text-right">{val||0}/{max}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* NDC Reference */}
      {a6Data?.ndc_reference?.dna_name && (
        <div className="bg-teal-50 border border-teal-100 rounded-xl p-3 mb-3">
          <p className="text-xs font-semibold text-teal-700">{a6Data.ndc_reference.dna_name}</p>
          <p className="text-xs text-teal-600 mt-0.5 line-clamp-2">{a6Data.ndc_reference.notes}</p>
        </div>
      )}

      {/* Blocker list */}
      {(elig.blockers||[]).length > 0 && (
        <div className="space-y-1.5 mb-3" data-testid="a6-blockers">
          {elig.blockers.map((b,i) => (
            <div key={i} className="flex items-start gap-2 px-2.5 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs">
              <AlertTriangle size={10} className="text-amber-500 mt-0.5 flex-shrink-0"/>
              <div>
                <p className="font-medium text-amber-700">{b.req}</p>
                <p className="text-amber-600 text-xs mt-0.5">{b.fix}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── LoA Governance Workflow ── */}
      <div className="border border-slate-200 rounded-xl overflow-hidden mt-3" data-testid="loa-workflow">
        <div className="bg-slate-50 px-4 py-2.5 flex items-center justify-between border-b border-slate-200">
          <p className="text-xs font-semibold text-slate-600">LoA Governance Workflow</p>
          {!loa?.exists ? (
            <button onClick={initiateLoa} data-testid="initiate-loa-btn"
              className="text-xs text-teal-600 border border-teal-200 rounded-lg px-2.5 py-1 hover:bg-teal-50">
              Initiate LoA
            </button>
          ) : (
            <button onClick={() => setShowEdit(!showEdit)} className="text-xs text-slate-400 hover:text-slate-700 px-2 py-1 rounded-lg hover:bg-slate-100">
              {showEdit ? "Close" : "Edit Fields"}
            </button>
          )}
        </div>

        <div className="p-4">
          {/* Stage Progress */}
          <div className="flex items-center mb-4 overflow-x-auto gap-1" data-testid="loa-stages">
            {LOA_STAGES.filter(s=>s!=="REJECTED").map((s, i) => {
              const done    = loaIdx > i;
              const current = loaIdx === i;
              const rejected= loa?.lifecycle_stage === "REJECTED";
              const color2  = rejected && current ? "#EF4444" : current ? "#0D9488" : done ? "#0D9488" : "#E2E8F0";
              return (
                <React.Fragment key={s}>
                  {i > 0 && <div className={`h-0.5 flex-1 min-w-2 ${done?"bg-teal-400":"bg-slate-200"}`}/>}
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center"
                      style={{backgroundColor: color2}}>
                      {done && !current ? <Check size={10} className="text-white" strokeWidth={3}/> :
                       current ? <span className="text-white text-xs font-bold">{i+1}</span> :
                       <span className="text-slate-400 text-xs">{i+1}</span>}
                    </div>
                    <p className={`text-xs mt-0.5 text-center max-w-12 leading-tight ${current?"text-teal-700 font-bold":"text-slate-400"}`}>
                      {s.replace(/_/g," ")}
                    </p>
                  </div>
                </React.Fragment>
              );
            })}
          </div>

          {loa?.exists ? (
            <>
              {/* Current LoA info */}
              <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
                {[["DNA Authority", loa.dna_authority], ["Submission Date", loa.submission_date],
                  ["App. Reference", loa.application_reference], ["Validity", loa.validity_period]].filter(([,v])=>v).map(([l,v])=>(
                  <div key={l}><span className="text-slate-400">{l}:</span> <strong className="text-slate-900">{String(v)}</strong></div>
                ))}
                {loa.corresponding_adjustment !== null && (
                  <div><span className="text-slate-400">Corresp. Adj.:</span> <strong className={loa.corresponding_adjustment?"text-emerald-600":"text-amber-600"}>{loa.corresponding_adjustment?"Yes":"No"}</strong></div>
                )}
              </div>

              {/* Advance stage */}
              {loa.lifecycle_stage !== "APPROVED" && loa.lifecycle_stage !== "REJECTED" && (
                <div className="flex gap-2 mb-3">
                  <select value={advForm.stage} onChange={e=>setAdvForm(f=>({...f,stage:e.target.value}))}
                    className="flex-1 h-8 px-2 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none">
                    <option value="">Advance to...</option>
                    {LOA_STAGES.filter(s => LOA_STAGES.indexOf(s) > loaIdx && s !== "NOT_INITIATED").map(s=>(
                      <option key={s} value={s}>{s.replace(/_/g," ")}</option>
                    ))}
                  </select>
                  <button onClick={advanceStage} disabled={advancing || !advForm.stage}
                    data-testid="advance-loa-btn"
                    className="px-3 h-8 bg-teal-600 text-white text-xs font-medium rounded-lg hover:bg-teal-700 disabled:opacity-50">
                    {advancing ? "..." : "Advance"}
                  </button>
                </div>
              )}

              {/* Document upload */}
              <div className="flex gap-2">
                <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                  data-testid="upload-loa-doc-btn"
                  className="flex-1 h-8 border border-teal-200 text-teal-600 text-xs rounded-lg hover:bg-teal-50 disabled:opacity-50">
                  {uploading ? "Uploading..." : "+ Upload LoA Document (PDF)"}
                </button>
                <input ref={fileInputRef} type="file" accept=".pdf" className="hidden" onChange={uploadDoc} />
              </div>
              {(loa.documents||[]).length > 0 && (
                <div className="mt-2 space-y-1">
                  {loa.documents.map(d => (
                    <div key={d.id} className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 rounded px-2 py-1">
                      <FileText size={10}/> {d.file_name} v{d.version}
                      <span className="font-mono text-slate-300 ml-auto">{d.sha256_hash?.slice(0,12)}...</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Edit form */}
              {showEdit && (
                <div className="mt-3 border-t border-slate-100 pt-3 space-y-2">
                  {[["dna_authority","DNA Authority","DFFE — South Africa"],
                    ["application_reference","Application Reference","NDC-A6-ZA-2026-001"],
                    ["validity_period","Validity Period","2026-2035"],
                    ["conditions","Conditions (optional)","Only renewables + waste projects"]].map(([k,l,p])=>(
                    <div key={k}>
                      <label className="text-xs text-slate-400 mb-0.5 block">{l}</label>
                      <input value={editForm[k]||""} onChange={e=>setEditForm(f=>({...f,[k]:e.target.value}))} placeholder={p}
                        className="w-full h-8 px-2 text-xs border border-slate-200 rounded-lg focus:outline-none"/>
                    </div>
                  ))}
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="ca" checked={editForm.corresponding_adjustment||false}
                      onChange={e=>setEditForm(f=>({...f,corresponding_adjustment:e.target.checked}))} />
                    <label htmlFor="ca" className="text-xs text-slate-500">Corresponding adjustment committed</label>
                  </div>
                  <button onClick={saveLoaFields} disabled={updating}
                    className="w-full h-8 bg-teal-600 text-white text-xs font-medium rounded-lg hover:bg-teal-700 disabled:opacity-50">
                    {updating?"Saving...":"Save LoA Fields"}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-4 text-xs text-slate-400">
              <p>LoA application not yet initiated.</p>
              <p className="mt-1">Click "Initiate LoA" to begin the governance workflow.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
// ── Outputs Tab (Project-Led Credit Issuance) ──────────────────────────────
function OutputsTab({ project, projectDbId, onRefresh }) {
  const navigate = useNavigate();
  const [check,    setCheck]    = useState(null);
  const [credits,  setCredits]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [issuing,  setIssuing]  = useState(false);
  const [listPrice, setListPrice] = useState("12");
  const [showList,  setShowList]  = useState(false);
  const [listing,  setListing]  = useState(false);

  const loadOutputs = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, crRes] = await Promise.allSettled([
        client.get(`/cai/projects/${projectDbId}/issuance-check`),
        project.mrv?.assessment_id
          ? client.get(`/credits/assessment/${project.mrv.assessment_id}`)
          : Promise.resolve(null),
      ]);
      if (cRes.status === "fulfilled") setCheck(cRes.value.data);
      if (crRes.status === "fulfilled" && crRes.value?.data) setCredits(crRes.value.data.credits || []);
    } catch {}
    setLoading(false);
  }, [projectDbId, project.mrv?.assessment_id]);

  useEffect(() => { loadOutputs(); }, [loadOutputs]);

  const handleIssue = async () => {
    setIssuing(true);
    try {
      const res = await client.post(`/cai/projects/${projectDbId}/issue-credits`);
      toast.success(res.data.message || "Credits issued!");
      onRefresh();
      loadOutputs();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "object" && detail.blockers) {
        toast.error(`${detail.blocker_count} prerequisite(s) not met. See checklist.`);
        setCheck(c => c ? {...c, is_ready: false, blockers: detail.blockers} : c);
      } else {
        toast.error(String(detail || "Credit issuance failed"));
      }
    }
    setIssuing(false);
  };

  const handleList = async (creditId) => {
    setListing(true);
    try {
      await client.post("/marketplace/list", {
        credit_id: creditId, price_per_tonne: Number(listPrice),
        description: `DPCS+ verified credits — ${project.name}`
      });
      toast.success(`Credits listed on marketplace at $${listPrice}/tCO₂e`);
      setShowList(false);
      loadOutputs();
    } catch (err) { toast.error(err.response?.data?.detail || "Listing failed"); }
    setListing(false);
  };

  const handleRetire = async (creditId) => {
    if (!window.confirm("Retire these credits? This is permanent and irreversible.")) return;
    try {
      await client.post(`/credits/retire/${creditId}`, { retirement_reason: "Internal offset", retired_by: project.organization || "DeCarbonIQ" });
      toast.success("Credits retired as carbon offset.");
      loadOutputs();
    } catch (err) { toast.error(err.response?.data?.detail || "Retirement failed"); }
  };

  if (loading) return <div className="h-48 bg-slate-100 rounded-xl animate-pulse" />;

  const issuedCredits = credits.filter(c => c.status !== "retired");
  const c = check || {};

  return (
    <div className="space-y-4" data-testid="outputs-tab">
      {/* Credit Issuance Section */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="text-sm font-bold text-slate-900">Carbon Credit Issuance</h4>
            <p className="text-xs text-slate-400 mt-0.5">Project-led issuance via CAI gateway — all prerequisites enforced</p>
          </div>
          <span className={`text-xs px-2.5 py-1 rounded-full font-semibold border ${c.is_ready ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
            {c.is_ready ? "Ready to Issue" : `${c.blocker_count || 0} Blocker(s)`}
          </span>
        </div>

        {/* Readiness Checklist */}
        {!c.is_ready && c.blockers?.length > 0 && (
          <div className="space-y-2 mb-4" data-testid="issuance-blockers">
            {c.blockers.map((b, i) => (
              <div key={i} className="flex items-start gap-2.5 px-3 py-2.5 bg-red-50 border border-red-200 rounded-xl text-xs">
                <X size={12} className="text-red-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-red-700">{b.requirement}</p>
                  <p className="text-red-600 mt-0.5">Current: {b.current}</p>
                  <p className="text-slate-500 mt-0.5">Fix: {b.fix}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Readiness Summary (all passed) */}
        {c.readiness_summary && (
          <div className="grid grid-cols-2 gap-1.5 mb-4" data-testid="readiness-summary">
            {[
              ["Stage VALUE_READY",       c.readiness_summary.stage],
              ["Value Pathway",           c.readiness_summary.pathway],
              ["Methodology Selected",    c.readiness_summary.methodology],
              ["Assessment Linked",       c.readiness_summary.assessment],
              ["MRV Completeness ≥ 50%",  c.readiness_summary.mrv_completeness],
              ["Verification ≥ System",   c.readiness_summary.verification],
              ["Reduction Quantified",    c.readiness_summary.reduction],
            ].map(([label, passed]) => (
              <div key={label} className={`flex items-center gap-1.5 text-xs px-2 py-1.5 rounded-lg ${passed ? "text-emerald-700 bg-emerald-50" : "text-red-600 bg-red-50"}`}>
                {passed ? <Check size={10} strokeWidth={3}/> : <X size={10}/>}
                {label}
              </div>
            ))}
          </div>
        )}

        {/* Issuance Summary (when ready) */}
        {c.is_ready && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4">
            <p className="text-xs font-semibold text-emerald-700 mb-2">Issuance Summary</p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {[
                ["Methodology",     c.methodology_id],
                ["Verification",    c.verification_level?.replace(/_/g," ")],
                ["Reduction basis", `${c.total_reduction_tco2e?.toFixed(2)} tCO₂e`],
                ["Estimated credits",`${c.estimated_credits?.toFixed(2)} tCO₂e (×${c.eligibility_factor})`],
              ].map(([l,v]) => (
                <div key={l}><span className="text-slate-500">{l}:</span> <strong className="text-emerald-800">{v}</strong></div>
              ))}
            </div>
          </div>
        )}

        {/* CTA — show when no active issued credits, show message when already exists */}
        {issuedCredits.length === 0 ? (
          <button onClick={handleIssue} disabled={issuing} data-testid="issue-credits-cta"
            className={`w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-bold rounded-xl transition-all ${
              c.is_ready
                ? "bg-[#059669] text-white hover:bg-emerald-700"
                : "bg-slate-100 text-slate-400 cursor-not-allowed"
            } disabled:opacity-60`}>
            {issuing ? <RefreshCw size={14} className="animate-spin"/> : <Leaf size={14}/>}
            {issuing ? "Issuing..." : c.is_ready ? "Generate Credits" : "Resolve blockers to generate credits"}
          </button>
        ) : (
          <div className="px-3 py-2.5 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-700 flex items-center gap-2">
            <Check size={12} strokeWidth={3} /> Credits already issued for this project. See below to manage them.
          </div>
        )}
      </div>

      {/* Issued Credits — post-issue actions */}
      {issuedCredits.map(credit => (
        <div key={credit.credit_id} className="bg-white border border-emerald-200 rounded-xl p-5" data-testid={`issued-credit-${credit.credit_id}`}>
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-xs text-slate-400">Issued Credit</p>
              <p className="text-sm font-bold font-mono text-[#059669]">{credit.credit_id}</p>
              <p className="text-xs text-slate-500 mt-0.5">{credit.methodology_name} · FY{credit.vintage_year}</p>
            </div>
            <div className="text-right">
              <MetricCard label="tCO₂e" value={credit.volume_tco2e?.toFixed(2)} color="text-slate-900" className="min-w-[80px]" />
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium mt-1 inline-block ${credit.status === "listed" ? "bg-blue-50 text-blue-700" : credit.status === "issued" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                {credit.status}
              </span>
            </div>
          </div>

          {/* Post-issue action choice */}
          {credit.status === "issued" && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Choose next action:</p>
              <div className="grid grid-cols-3 gap-2">
                <button
                  className="flex flex-col items-center gap-1 px-3 py-3 border-2 border-slate-200 rounded-xl hover:border-slate-400 text-center"
                  data-testid="hold-btn">
                  <span className="text-lg">🔒</span>
                  <span className="text-xs font-semibold text-slate-700">Hold</span>
                  <span className="text-xs text-slate-400">Keep for financing</span>
                </button>
                <button onClick={() => setShowList(!showList)} data-testid="list-marketplace-btn"
                  className="flex flex-col items-center gap-1 px-3 py-3 border-2 border-blue-200 bg-blue-50 rounded-xl hover:border-blue-400 text-center">
                  <span className="text-lg">🏪</span>
                  <span className="text-xs font-semibold text-blue-700">List</span>
                  <span className="text-xs text-blue-500">Marketplace</span>
                </button>
                <button onClick={() => handleRetire(credit.credit_id)} data-testid="retire-btn"
                  className="flex flex-col items-center gap-1 px-3 py-3 border-2 border-amber-200 bg-amber-50 rounded-xl hover:border-amber-400 text-center">
                  <span className="text-lg">♻️</span>
                  <span className="text-xs font-semibold text-amber-700">Retire</span>
                  <span className="text-xs text-amber-500">Internal offset</span>
                </button>
              </div>

              {showList && (
                <div className="mt-2 flex gap-2 border border-blue-200 bg-blue-50 rounded-xl p-3">
                  <div className="flex-1">
                    <label className="text-xs font-medium text-blue-700 mb-1 block">Listing price (USD/tCO₂e)</label>
                    <input type="number" value={listPrice} onChange={e => setListPrice(e.target.value)} placeholder="e.g. 12"
                      className="w-full h-9 px-3 border border-blue-200 rounded-lg text-sm bg-white focus:outline-none" />
                  </div>
                  <div className="flex flex-col gap-1 justify-end">
                    <button onClick={() => handleList(credit.credit_id)} disabled={listing}
                      className="px-3 h-9 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50">
                      {listing ? "..." : "Confirm"}
                    </button>
                    <button onClick={() => setShowList(false)} className="px-3 h-7 text-slate-400 text-xs rounded-lg hover:bg-slate-100">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {credit.status === "listed" && (
            <div className="px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
              Credits are listed on the marketplace. Buyers can now purchase and retire them.
              <button onClick={() => navigate("/marketplace")} className="ml-2 underline">View Marketplace →</button>
            </div>
          )}
        </div>
      ))}

      {/* Non-credit outputs (always available) */}
      <SectionCard>

        <h4 className="text-sm font-bold text-slate-900 mb-3">Other Outputs</h4>
        <div className="space-y-2">
          {[
            { label:"Investment Pack & FRS", available: !!project.mrv?.assessment_id, action:() => navigate(`/assessments/${project.mrv.assessment_id}?tab=investment-pack`), icon:BarChart2 },
            { label:"ESG Performance Report", available: project.stage !== "INTAKE", action:() => navigate(`/assessments/${project.mrv?.assessment_id}?tab=reports`), icon:FileText },
          ].map((o, i) => (
            <div key={i} className={`flex items-center gap-3 p-3 rounded-xl border ${o.available ? "border-slate-200 hover:border-[#059669] cursor-pointer" : "border-slate-100 opacity-50"}`}
              onClick={o.available ? o.action : undefined}>
              <o.icon size={14} className={o.available ? "text-[#059669]" : "text-slate-400"} />
              <span className="text-sm font-medium text-slate-900">{o.label}</span>
              {o.available && <ChevronRight size={12} className="text-[#059669] ml-auto" />}
            </div>
          ))}
        </div>
      
      </SectionCard>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────
export default function CAIProjectWorkspace() {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const [project, setProject]  = useState(null);
  const [loading, setLoading]  = useState(true);
  const [methodologies, setMths] = useState([]);
  const [mrv, setMrv]          = useState(null);
  const [advancing, setAdv]    = useState(false);
  const [classifying, setClass]= useState(false);
  const [structForm, setStructForm] = useState({});
  const [showLinkModal, setLinkModal] = useState(false);
  const [assessments, setAssessments] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, mRes, aRes] = await Promise.allSettled([
        client.get(`/cai/projects/${id}`),
        client.get("/credits/methodologies"),
        client.get("/assessments"),
      ]);
      if (pRes.status === "fulfilled") {
        setProject(pRes.value.data);
        const p = pRes.value.data;
        setStructForm({
          methodology_id: p.methodology?.methodology_id ? null : null,
          methodology_status: p.methodology?.status || "not_defined",
          additionality_status: p.additionality?.status || "not_assessed",
        });
      }
      if (mRes.status === "fulfilled") setMths(mRes.value.data || []);
      if (aRes.status === "fulfilled") setAssessments((aRes.value.data||[]).filter(a=>["issued","approved","analysis","review"].includes(a.status)));
    } catch {}
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleExportCaseStudy = async (fmt = "json") => {
    try {
      const projectId = project?.project_id;
      if (!projectId) return;
      const res = await client.get(`/cai/case-study/${projectId}?fmt=${fmt}`, { responseType: "blob" });
      const ext  = fmt === "text" ? "txt" : "json";
      const mime = fmt === "text" ? "text/plain" : "application/json";
      const url  = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `case-study-${projectId}-${new Date().toISOString().slice(0,10)}.${ext}`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`Case study exported (${fmt.toUpperCase()})`);
    } catch { toast.error("Case study export failed"); }
  };

  const handleAdvance = async () => {
    setAdv(true);
    try {
      const res = await client.post(`/cai/projects/${id}/advance-stage`);
      toast.success(`Advanced to ${res.data.new_stage}`);
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "object" && detail.failures) {
        toast.error(detail.failures[0] || "Prerequisite not met");
      } else {
        toast.error(String(detail || "Failed to advance"));
      }
    }
    setAdv(false);
  };

  const handleScreen = async () => {
    try {
      const res = await client.post(`/cai/projects/${id}/screen`);
      toast.success(`Eligibility: ${res.data.status} (${res.data.score?.toFixed(0)}/100)`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Screening failed"); }
  };

  const handleSaveStructuring = async () => {
    try {
      const mthId = structForm.methodology_name
        ? methodologies.find(m=>m.name===structForm.methodology_name)?.id
        : null;
      await client.patch(`/cai/projects/${id}/structuring`, {
        methodology_id: mthId || undefined,
        methodology_status: structForm.methodology_status || undefined,
        additionality_status: structForm.additionality_status || undefined,
      });
      toast.success("Structuring saved");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };

  const handleLinkAssessment = async (assessmentId) => {
    try {
      const res = await client.post(`/cai/projects/${id}/link-assessment`, { assessment_id: assessmentId });
      toast.success(`MRV linked: ${res.data.mrv_completeness?.toFixed(0)}% completeness`);
      setLinkModal(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Link failed"); }
  };

  const handleClassify = async () => {
    setClass(true);
    try {
      const res = await client.post(`/cai/projects/${id}/classify`);
      toast.success(`Classified: ${res.data.pathway_label}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Classification failed"); }
    setClass(false);
  };

  const handleCarsUpdate = (carsData) => {
    setProject(p => p ? { ...p, cars_score: carsData.cars_score, cars_gaps: carsData.gaps, cars_next_actions: carsData.next_actions } : p);
  };

  if (loading) return (
    <div className="p-8 space-y-4">
      <div className="h-8 w-48 bg-slate-200 rounded animate-pulse" />
      <div className="h-64 bg-slate-100 rounded-xl animate-pulse" />
    </div>
  );
  if (!project) return <div className="p-8 text-center text-slate-400">Project not found</div>;

  const p = project;
  const stageIdx = LIFECYCLE_STAGES.indexOf(p.stage);
  const isLastStage = stageIdx >= LIFECYCLE_STAGES.length - 1;

  return (
    <div className="p-6 lg:p-8 min-h-screen" data-testid="cai-workspace">
      {/* Header */}
      <div className="mb-5">
        <button onClick={() => navigate("/cai")} className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-700 mb-3">
          <ArrowLeft size={13} /> Control Tower
        </button>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              
      <PageHeader
        title={p.name}
        backUrl="/cai"
        backLabel="Control Tower"
      />
              <span className="text-xs font-mono px-2 py-0.5 bg-slate-100 rounded text-slate-500">{p.project_id}</span>
              <span className={`flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${STAGE_COLORS[p.stage]?.replace("bg-","text-white bg-")||"bg-slate-100 text-slate-600"} text-white`}>
                {p.stage.replace(/_/g," ")}
              </span>
            </div>
            <p className="text-xs text-slate-500">{p.sector} · {p.country} · {p.project_type}</p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            {/* Case study export — available when project is sufficiently advanced */}
            {["ARTICLE6_SUBMISSION","ISSUED","MRV_ACTIVE","VERIFICATION","VALUE_READY"].includes(p.stage) && (
              <div className="relative group">
                <button className="flex items-center gap-1.5 px-3 py-2 bg-teal-600 text-white text-xs font-semibold rounded-xl hover:bg-teal-700"
                  data-testid="case-study-export-btn">
                  <FileDown size={12} /> Case Study
                </button>
                <div className="absolute right-0 top-9 z-20 bg-white border border-slate-200 rounded-xl shadow-lg py-1.5 min-w-44 hidden group-hover:block">
                  <div className="px-3 py-1 mb-1 border-b border-slate-100">
                    <p className="text-xs text-slate-400 uppercase font-semibold">Export Format</p>
                  </div>
                  <button onClick={() => handleExportCaseStudy("executive")}
                    className="w-full text-left px-3 py-2 text-xs text-slate-900 hover:bg-teal-50 font-semibold flex items-center gap-2"
                    data-testid="case-study-executive-btn">
                    <span className="text-xs px-1.5 py-0.5 rounded bg-teal-100 text-teal-700 font-bold">BOARD</span>
                    Executive Brief
                  </button>
                  <button onClick={() => handleExportCaseStudy("json")}
                    className="w-full text-left px-3 py-2 text-xs text-slate-500 hover:bg-slate-50"
                    data-testid="case-study-json-btn">
                    JSON (structured)
                  </button>
                  <button onClick={() => handleExportCaseStudy("text")}
                    className="w-full text-left px-3 py-2 text-xs text-slate-500 hover:bg-slate-50"
                    data-testid="case-study-text-btn">
                    Text (internal)
                  </button>
                </div>
              </div>
            )}
            <button onClick={handleAdvance} disabled={advancing || isLastStage} data-testid="advance-stage-btn"
              className="flex items-center gap-1.5 px-3 py-2 bg-[#059669] text-white text-xs font-medium rounded-xl hover:bg-emerald-700 disabled:opacity-50">
              <ArrowUpRight size={13} className={advancing?"animate-spin":""} />
              {advancing ? "Advancing..." : `→ Next Stage`}
            </button>
          </div>
        </div>
        <div className="mt-3">
          <StageProgress currentStage={p.stage} />
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview">
        <TabsList className="bg-transparent border-0 h-auto p-0 gap-0 w-full overflow-x-auto mb-5">
          {[["overview","Overview"],["structuring","Structuring"],["mrv","MRV & Data"],["evidence","Evidence"],["verification","Verification"],["value","Value Pathway"],["outputs","Outputs"]].map(([v,l])=>(
            <TabsTrigger key={v} value={v}
              className="rounded-none border-b-2 border-transparent px-4 py-2.5 text-xs font-medium text-slate-500 data-[state=active]:border-[#059669] data-[state=active]:text-[#059669] data-[state=active]:bg-transparent">
              {l}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <CARSPanel project={p} onRecompute={handleCarsUpdate} />
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <h4 className="text-sm font-bold text-slate-900 mb-3">Project Identity</h4>
              <div className="grid grid-cols-2 gap-2">
                {[["Name",p.name],["Type",p.project_type],["Sector",p.sector],["Country",p.country],["Organization",p.organization],["Stage",p.stage]].map(([l,v])=>(
                  <div key={l}><p className="text-xs text-slate-400">{l}</p><p className="text-sm font-semibold text-slate-900">{String(v||"—")}</p></div>
                ))}
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <h4 className="text-sm font-bold text-slate-900 mb-3">Risk Flags</h4>
            {(p.risk?.flags||[]).length === 0
              ? <p className="text-xs text-emerald-600 bg-emerald-50 rounded-lg p-2">No risk flags. Good standing.</p>
              : (p.risk?.flags||[]).map((f,i) => (
                <div key={i} className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg p-2 mb-1">
                  <AlertTriangle size={11} className="mt-0.5 flex-shrink-0" />{f}
                </div>
              ))}
          </div>
          {(p.logs||[]).length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <h4 className="text-sm font-bold text-slate-900 mb-3">Activity Log</h4>
              <div className="space-y-2">
                {p.logs.map((l,i)=>(
                  <div key={i} className="flex gap-3 text-xs py-1.5 border-b border-slate-50">
                    <div className="w-1 bg-blue-300 rounded-full flex-shrink-0" />
                    <p className="text-slate-600">{l.description}</p>
                    <span className="text-slate-300 ml-auto flex-shrink-0">{l.timestamp?.slice(0,16)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* STRUCTURING */}
        <TabsContent value="structuring" className="space-y-4">
          <SectionCard>

            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-bold text-slate-900">Eligibility Screening</h4>
              <button onClick={handleScreen}
                className="flex items-center gap-1 px-3 py-1.5 bg-[#0F172A] text-white text-xs font-medium rounded-lg hover:bg-slate-800">
                Run Screening
              </button>
            </div>
            {p.eligibility?.status !== "not_assessed" && (
              <div className={`px-3 py-2.5 rounded-xl border text-sm font-semibold mb-3 ${p.eligibility.status==="eligible"?"bg-emerald-50 border-emerald-200 text-emerald-700":p.eligibility.status==="conditional"?"bg-amber-50 border-amber-200 text-amber-700":"bg-red-50 border-red-200 text-red-700"}`}>
                {p.eligibility.status.toUpperCase()} — Score: {p.eligibility.score?.toFixed(0)}/100
              </div>
            )}
            {(p.eligibility?.constraints||[]).map((c,i)=>(
              <p key={i} className="text-xs text-slate-500 flex items-start gap-1.5"><Info size={10} className="mt-0.5 text-amber-500 flex-shrink-0"/>{c}</p>
            ))}
                    </SectionCard>

          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h4 className="text-sm font-bold text-slate-900 mb-4">Methodology Selection</h4>
            <div className="space-y-3">
              <div><label className="text-xs font-medium text-slate-600 mb-1 block">DPCS+ Methodology</label>
                <select value={structForm.methodology_name||""} onChange={e=>setStructForm(f=>({...f,methodology_name:e.target.value}))}
                  className="w-full h-10 px-3 border border-slate-200 rounded-xl text-sm bg-white focus:outline-none">
                  <option value="">Select methodology...</option>
                  {methodologies.map(m=><option key={m.id} value={m.name}>{m.methodology_id} — {m.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-xs font-medium text-slate-600 mb-1 block">Methodology Status</label>
                  <select value={structForm.methodology_status||""} onChange={e=>setStructForm(f=>({...f,methodology_status:e.target.value}))}
                    className="w-full h-10 px-3 border border-slate-200 rounded-xl text-sm bg-white focus:outline-none">
                    {["not_defined","selected","approved"].map(s=><option key={s} value={s}>{s.replace(/_/g," ")}</option>)}
                  </select>
                </div>
                <div><label className="text-xs font-medium text-slate-600 mb-1 block">Additionality</label>
                  <select value={structForm.additionality_status||""} onChange={e=>setStructForm(f=>({...f,additionality_status:e.target.value}))}
                    className="w-full h-10 px-3 border border-slate-200 rounded-xl text-sm bg-white focus:outline-none">
                    {["not_assessed","confirmed","likely","weak","not_additional"].map(s=><option key={s} value={s}>{s.replace(/_/g," ")}</option>)}
                  </select>
                </div>
              </div>
              <button onClick={handleSaveStructuring}
                className="w-full h-10 bg-[#059669] text-white text-sm font-medium rounded-xl hover:bg-emerald-700">
                Save Structuring
              </button>
            </div>
          </div>
        </TabsContent>

        {/* MRV & DATA */}
        <TabsContent value="mrv" className="space-y-4">
          <SectionCard>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="text-sm font-bold text-slate-900">MRV & Data Status</h4>
                <p className="text-xs text-slate-400 mt-0.5">Reads from V1 baseline engine — no duplication</p>
              </div>
              <button onClick={() => setLinkModal(true)} data-testid="link-assessment-btn"
                className="flex items-center gap-1 px-3 py-1.5 bg-[#0F172A] text-white text-xs font-medium rounded-lg hover:bg-slate-800">
                Link Assessment
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              {[["MRV Status", p.mrv?.status?.replace(/_/g," ")], ["Completeness", `${p.mrv?.completeness?.toFixed(0)||0}%`],
                ["Assessment ID", p.mrv?.assessment_id||"Not linked"], ["Stage", p.stage]].map(([l,v])=>(
                <div key={l} className="bg-slate-50 rounded-xl p-3">
                  <p className="text-xs text-slate-400">{l}</p>
                  <p className="text-sm font-semibold text-slate-900">{String(v||"—")}</p>
                </div>
              ))}
            </div>
            {p.mrv?.completeness != null && (
              <div>
                <div className="flex justify-between text-xs mb-1 text-slate-500">
                  <span>MRV Completeness</span><span>{p.mrv?.completeness?.toFixed(0)}%</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-[#059669]" style={{width:`${p.mrv?.completeness||0}%`}} />
                </div>
              </div>
            )}
            {p.mrv?.assessment_id && (
              <button onClick={() => navigate(`/assessments/${p.mrv.assessment_id}`)}
                className="mt-3 flex items-center gap-1 text-xs text-[#059669] hover:text-emerald-700">
                <ArrowUpRight size={11} /> Open V1 Assessment
              </button>
            )}
          </SectionCard>
        </TabsContent>
        <TabsContent value="evidence">

        {/* EVIDENCE */}
          {p.mrv?.assessment_id ? (
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-bold text-slate-900">Evidence Vault</h4>
                <button onClick={() => navigate(`/assessments/${p.mrv.assessment_id}?tab=evidence`)}
                  className="flex items-center gap-1 text-xs text-[#059669] hover:text-emerald-700">
                  <ArrowUpRight size={11} /> Manage Evidence in V1
                </button>
              </div>
              <p className="text-xs text-slate-500 bg-slate-50 rounded-xl p-4">
                Evidence is managed in the linked V1 assessment workspace. All evidence, validation, and anomaly data is read from V1 — not duplicated here.
                <br/><br/>
                <strong>Assessment:</strong> ID {p.mrv.assessment_id}
              </p>
            </div>
          ) : (
            <div className="text-center py-12 text-slate-400">
              <FileText size={28} className="mx-auto mb-2 opacity-30"/>
              <p className="text-sm">Link a V1 assessment in the MRV tab to access evidence.</p>
            </div>
          )}
        </TabsContent>

        {/* VERIFICATION */}
        <TabsContent value="verification">
          {p.mrv?.assessment_id ? (
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-bold text-slate-900">Verification Status (V1)</h4>
                <button onClick={() => navigate(`/assessments/${p.mrv.assessment_id}?tab=intelligence`)}
                  className="flex items-center gap-1 text-xs text-[#059669] hover:text-emerald-700">
                  <ArrowUpRight size={11} /> Open Intelligence Tab
                </button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[["Verification Level", p.verification?.level?.replace(/_/g," ")],
                  ["Confidence Score", `${p.verification?.confidence_score?.toFixed(0)||"—"}%`]].map(([l,v])=>(
                  <div key={l} className="bg-slate-50 rounded-xl p-3">
                    <p className="text-xs text-slate-400">{l}</p>
                    <p className="text-sm font-bold text-slate-900 capitalize">{String(v||"—")}</p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-3 italic">
                Verification data sourced from V1 Confidence Engine. Upgrade verification in the V1 assessment workspace Intelligence tab.
              </p>
            </div>
          ) : (
            <div className="text-center py-12 text-slate-400">
              <Shield size={28} className="mx-auto mb-2 opacity-30"/>
              <p className="text-sm">Link a V1 assessment to see verification status.</p>
            </div>
          )}
        </TabsContent>

        {/* VALUE PATHWAY */}
        <TabsContent value="value" className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-bold text-slate-900">Value Classification Engine</h4>
              <button onClick={handleClassify} disabled={classifying} data-testid="classify-btn"
                className="flex items-center gap-1 px-3 py-1.5 bg-[#059669] text-white text-xs font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50">
                {classifying ? <RefreshCw size={11} className="animate-spin"/> : <Target size={11}/>}
                Run Classification
              </button>
            </div>
            {p.classification?.value_pathway && (() => {
              const cfg = PATHWAY_CFG[p.classification.value_pathway] || {};
              return (
                <div>
                  <div className={`flex items-center gap-3 p-4 rounded-xl border mb-4 ${cfg.color}`}>
                    <div>
                      <p className="text-base font-bold">{cfg.label}</p>
                      <p className="text-xs mt-0.5 opacity-75">{p.classification.pathway_description}</p>
                    </div>
                    <span className="ml-auto text-sm font-bold">{((p.classification.confidence||0)*100).toFixed(0)}%</span>
                  </div>
                  {p.metrics?.credits_estimate > 0 && p.classification.value_pathway === "carbon_credit_candidate" && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm">
                      Credit estimate: <strong>{p.metrics.credits_estimate?.toFixed(1)} tCO₂e</strong>
                    </div>
                  )}
                </div>
              );
            })()}
            {!p.classification?.value_pathway && (
              <p className="text-xs text-slate-400 italic">Run classification to determine the value pathway for this project.</p>
            )}
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h4 className="text-sm font-bold text-slate-900 mb-3">Article 6 Assessment</h4>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div className="bg-slate-50 rounded-xl p-3">
                <p className="text-xs text-slate-400">Article 6 Status</p>
                <p className="text-sm font-semibold capitalize">{p.article6?.status?.replace(/_/g," ")}</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-3">
                <p className="text-xs text-slate-400">A6 Readiness Score</p>
                <p className="text-sm font-semibold">{fmt(p.article6?.readiness_score)}/100</p>
              </div>
            </div>
            <Article6Panel projectDbId={parseInt(id)} article6Score={p.article6?.readiness_score} onRefresh={load} />
          </div>

          {/* ITMO Sovereign Transfer — only shows when authorized */}
          {p.article6?.status === "approved" && (
            <ITMOPanel projectDbId={parseInt(id)} onRefresh={load} />
          )}
        </TabsContent>

        {/* OUTPUTS */}
        <TabsContent value="outputs">
          <OutputsTab project={p} projectDbId={parseInt(id)} onRefresh={load} />
        </TabsContent>
      </Tabs>

      {/* Link Assessment Modal */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between mb-4">
              <h3 className="text-base font-bold text-slate-900">Link V1 Baseline Assessment</h3>
              <button onClick={() => setLinkModal(false)} className="text-slate-400">✕</button>
            </div>
            <div className="space-y-2">
              {assessments.map(a => (
                <button key={a.id} onClick={() => handleLinkAssessment(a.id)}
                  className="w-full flex items-center justify-between p-3 bg-white border border-slate-200 rounded-xl hover:border-[#059669] text-left">
                  <div>
                    <p className="text-sm font-medium text-slate-900">{a.title}</p>
                    <p className="text-xs text-slate-400">{a.status} {a.reporting_year ? `· FY${a.reporting_year}` : ""}</p>
                  </div>
                  <ChevronRight size={14} className="text-[#059669]" />
                </button>
              ))}
              {assessments.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No eligible assessments found.</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
